import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { session } from './api/client.js';
import { auth } from './api/resources.js';
import { useAsync } from './lib/hooks.js';
import { Shell } from './components/shell.js';
import { Loading } from './components/ui.js';
import { ToastProvider } from './components/toast.js';
import { ApiKeysPage, ApiLogsPage } from './pages/developers.js';
import { ExplorerPage } from './pages/explorer.js';
import { McpPage } from './pages/mcp.js';
import { IntegrationsPage } from './pages/integrations.js';
import { InstanceDetailPage } from './pages/instance-detail.js';
import { InstancesPage } from './pages/instances.js';
import { LoginPage } from './pages/auth/login.js';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/auth/password.js';
import { SignupPage } from './pages/auth/signup.js';
import { VerifyEmailPage } from './pages/auth/verify-email.js';
import { MessagesPage } from './pages/messages.js';
import { OverviewPage } from './pages/overview.js';
import { AuditPage } from './pages/audit.js';
import { NotFoundPage, SettingsPage } from './pages/settings.js';
import { WebhooksPage } from './pages/webhooks.js';
import { NotificationsPage, SupportPage, TeamPage, UsagePage } from './pages/account.js';
import { BillingPage } from './pages/billing.js';

/**
 * Plano de operação (PRD §58).
 *
 * Carregado sob demanda: o navegador de um cliente nunca baixa o código das telas que
 * leem todos os clientes. O controle de acesso continua sendo do servidor — este `lazy`
 * é sobre o que trafega, não sobre quem pode.
 */
const AdminApp = lazy(() => import('./pages/admin/index.js'));

/** Sends an unauthenticated visitor to login, preserving nothing sensitive in the URL. */
function RequireSession({ children }: { children: JSX.Element }) {
  return session.token ? children : <Navigate to="/login" replace />;
}

/**
 * O gate de cadastro: quem ainda não confirmou o e-mail cai na verificação antes de ver
 * o console. O servidor também recusa criar instância e API key sem isso — a tela é a
 * forma educada de dizer o que ele diria com 403.
 */
function RequireVerifiedEmail({ children }: { children: JSX.Element }) {
  const me = useAsync(() => auth.me(), []);
  if (me.loading) return <Loading />;
  if (me.data && me.data.emailVerified === false) return <Navigate to="/verify-email" replace />;
  return children;
}

export function App() {
  return (
    <ToastProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot" element={<ForgotPasswordPage />} />
        <Route path="/reset" element={<ResetPasswordPage />} />
        <Route
          path="/verify-email"
          element={
            <RequireSession>
              <VerifyEmailPage />
            </RequireSession>
          }
        />
        <Route
          path="/admin/*"
          element={
            <RequireSession>
              <Suspense fallback={<Loading label="Abrindo o plano de operação…" />}>
                <AdminApp />
              </Suspense>
            </RequireSession>
          }
        />
        <Route
          element={
            <RequireSession>
              <RequireVerifiedEmail>
                <Shell />
              </RequireVerifiedEmail>
            </RequireSession>
          }
        >
          <Route index element={<OverviewPage />} />
          <Route path="instances" element={<InstancesPage />} />
          <Route path="instances/:instanceId" element={<InstanceDetailPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="webhooks" element={<WebhooksPage />} />
          <Route path="developers/keys" element={<ApiKeysPage />} />
          <Route path="developers/logs" element={<ApiLogsPage />} />
          <Route path="developers/explorer" element={<ExplorerPage />} />
          <Route path="developers/mcp" element={<McpPage />} />
          <Route path="integrations" element={<IntegrationsPage />} />
          <Route path="usage" element={<UsagePage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </ToastProvider>
  );
}
